
"use client";

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MOCK_CAR_MAKES, type CarMake, type CarModel } from '@/app/lib/mock-db';
import { Camera, Car, Check, Loader2, Lightbulb, Sparkles, Info, Link as LinkIcon, AlertCircle, Trash2, ArrowLeft, ArrowRight, LogIn, UserPlus, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getContextualTips } from '@/app/lib/tips-data';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useCountry } from '@/context/CountryContext';
import { fetchCarMakes } from '@/lib/car-makes';
import { fetchCarModels, fetchCarTypes, type CarTypeOption } from '@/lib/car-taxonomy';
import { getStoredAccessToken, readStoredSession } from '@/lib/auth/session';
import { getCitiesForCountry } from '@/lib/country-cities';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export default function SellClient() {
  const { toast } = useToast();
  const router = useRouter();
  const { selectedCountryCode } = useCountry();

  // ── Auth guard ──────────────────────────────────────────────────────────────
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const session = readStoredSession();
    setIsAuthenticated(session.isAuthenticated);
    setAuthChecked(true);
    // Listen for login events (e.g. if user logs in from another tab)
    const handler = () => {
      const s = readStoredSession();
      setIsAuthenticated(s.isAuthenticated);
    };
    window.addEventListener('s360-auth-changed', handler);
    return () => window.removeEventListener('s360-auth-changed', handler);
  }, []);

  const goToLogin = (mode: 'login' | 'register' = 'login') => {
    // Save return URL so login page can redirect back
    if (typeof window !== 'undefined') {
      localStorage.setItem('s360_return_url', '/sell');
    }
    router.push(mode === 'register' ? '/register' : '/login?next=/sell');
  };
  // ────────────────────────────────────────────────────────────────────────────

  const [carMakes, setCarMakes] = useState<CarMake[]>(MOCK_CAR_MAKES);
  const [carTypes, setCarTypes] = useState<CarTypeOption[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [importUrl, setImportUrl] = useState('');
  const [customCity, setCustomCity] = useState(false);
  const [fuelTypes, setFuelTypes] = useState<Array<{ value: string; label: string; label_ar: string }>>([
    { value: 'petrol', label: 'Petrol', label_ar: 'بنزين' },
    { value: 'hybrid', label: 'Hybrid', label_ar: 'هايبرد' },
    { value: 'diesel', label: 'Diesel', label_ar: 'ديزل' },
    { value: 'electric', label: 'Electric', label_ar: 'كهربائي' },
  ]);
  const [transmissions, setTransmissions] = useState<Array<{ value: string; label: string; label_ar: string }>>([
    { value: 'automatic', label: 'Automatic', label_ar: 'أوتوماتيك' },
    { value: 'manual', label: 'Manual', label_ar: 'عادي' },
  ]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    car_type_id: '',
    make_id: '',
    model_id: '',
    year: '',
    price: '',
    mileage: '',
    fuel_type: 'petrol',
    transmission: 'automatic',
    city: '',
    description: '',
    image_url: ''
  });

  // State for inline advice
  const [localAdvice, setLocalAdvice] = useState<string | null>(null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchCarMakes(), fetchCarTypes()]).then(([makesRows, typesRows]) => {
      if (!alive) return;
      if (makesRows.length > 0) setCarMakes(makesRows);
      if (typesRows.length > 0) setCarTypes(typesRows);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/marketplace/listings/metadata/`, { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((payload) => {
        if (!alive || !payload) return;
        if (Array.isArray(payload.fuel_types)) setFuelTypes(payload.fuel_types);
        if (Array.isArray(payload.transmissions)) setTransmissions(payload.transmissions);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const urls = imageFiles.map((file) => URL.createObjectURL(file));
    setImagePreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [imageFiles]);

  useEffect(() => {
    if (!formData.make_id) {
      setCarModels([]);
      return;
    }
    let alive = true;
    fetchCarModels(formData.make_id).then((rows) => {
      if (alive) setCarModels(rows);
    });
    return () => { alive = false; };
  }, [formData.make_id]);

  const selectedMakeModels = carModels;
  const cityOptions = getCitiesForCountry(selectedCountryCode);
  const steps = [
    { title: 'طريقة الإدخال', hint: 'ابدأ يدوياً أو من رابط' },
    { title: 'بيانات السيارة', hint: 'الماركة والموديل والسنة' },
    { title: 'المواصفات', hint: 'السعر والممشى والناقل' },
    { title: 'الموقع والصور', hint: 'المدينة والوصف والصور' },
    { title: 'المراجعة', hint: 'راجع وانشر الإعلان' },
  ];

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    try {
      const response = await fetch('/api/import-listing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: importUrl, country_code: selectedCountryCode })
      });

      const result = await response.json();

      if (result.success && result.data) {
        setFormData((prev) => ({
          ...prev,
          title: result.data.title || '',
          description: result.data.description || '',
          price: result.data.price ? String(result.data.price) : '',
          mileage: result.data.mileage ? String(result.data.mileage) : prev.mileage,
          image_url: result.data.image || ''
        }));
        setIsLinkDialogOpen(false);
        toast({
          title: "تم الاستيراد بنجاح",
          description: "تم جلب بعض البيانات من الرابط. يرجى مراجعتها وتكملة النقص.",
        });
      } else {
        throw new Error(result.error || 'فشل الاستيراد');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "تنبيه",
        description: error.message || "لا يمكن جلب البيانات تلقائياً. يرجى الإدخال يدوياً.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = getStoredAccessToken();
      if (!token) {
        throw new Error('يرجى تسجيل الدخول أولاً حتى تتمكن من نشر الإعلان.');
      }

      const selectedMake = carMakes.find((m) => m.id === formData.make_id);
      const selectedType = carTypes.find((t) => t.id === formData.car_type_id);
      const selectedModel = selectedMakeModels.find((m) => m.id === formData.model_id);

      // Validate required fields before submitting
      if (!formData.make_id) throw new Error('يرجى اختيار ماركة السيارة.');
      if (selectedMakeModels.length > 0 && !formData.model_id) throw new Error('يرجى اختيار موديل السيارة.');
      if (!formData.year) throw new Error('يرجى إدخال سنة الصنع.');
      if (!formData.price) throw new Error('يرجى إدخال السعر المطلوب.');
      if (!formData.city.trim()) throw new Error('يرجى اختيار مدينة السيارة.');
      if (!formData.description.trim()) throw new Error('يرجى كتابة وصف السيارة.');

      const payload: Record<string, unknown> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: Number(formData.price),
        make_id: formData.make_id,
        make: selectedMake?.name_ar || selectedMake?.name_en || '',
        model_id: selectedModel?.id || formData.model_id,
        model: selectedModel?.name_ar || selectedModel?.name_en || formData.model_id,
        year: Number(formData.year),
        mileage: Number(formData.mileage || 0),
        fuel_type: formData.fuel_type,
        transmission: formData.transmission,
        city: formData.city.trim(),
        country_code: selectedCountryCode || 'OM',
      };

      if (selectedType) {
        payload.car_type_id = selectedType.id;
        payload.car_type = selectedType.name_ar || selectedType.name_en;
      }

      const body = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          body.append(key, String(value));
        }
      });
      if (importUrl.trim()) body.append('imported_from_url', importUrl.trim());
      if (formData.image_url.trim()) body.append('image_url', formData.image_url.trim());
      imageFiles.slice(0, 30).forEach((file) => body.append('images', file));

      const response = await fetch(`${API}/api/marketplace/listings/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Country-Code': selectedCountryCode || 'OM',
        },
        body,
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || (result && result.status === 'error')) {
        throw new Error(
          result?.message ||
          result?.detail ||
          'تعذر نشر الإعلان حالياً. يرجى مراجعة البيانات ثم المحاولة مرة أخرى.'
        );
      }

      setLoading(false);
      setSubmitted(true);
      toast({
        title: "تم بنجاح",
        description: "تم نشر إعلانك في سوق السيارات وسيظهر للمشترين ضمن قسم الشراء.",
      });
    } catch (error: any) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "تعذر النشر",
        description: error?.message || "حدث خطأ غير متوقع أثناء نشر الإعلان.",
      });
    }
  };

  const handleImageFiles = (files: FileList | null) => {
    if (!files) return;
    const selected = Array.from(files).filter((file) => file.type.startsWith('image/'));
    setImageFiles((current) => [...current, ...selected].slice(0, 30));
    setFormData((prev) => ({ ...prev, image_url: '' }));
  };

  const removeImageFile = (index: number) => {
    setImageFiles((current) => current.filter((_, i) => i !== index));
  };

  const optionLabel = (item: { value: string; label: string; label_ar: string }) => item.label_ar || item.label || item.value;

  const canMoveNext = () => {
    if (currentStep === 0) return formData.title.trim().length > 0 || importUrl.trim().length > 0;
    // Step 1: make is required; model is required only when models have loaded
    if (currentStep === 1) return Boolean(formData.make_id) && (selectedMakeModels.length === 0 || Boolean(formData.model_id));
    // Step 2: year and price are both required
    if (currentStep === 2) return Boolean(formData.year && formData.price);
    // Step 3: city and description are required
    if (currentStep === 3) return Boolean(formData.city.trim() && formData.description.trim());
    return true;
  };

  const getMissingFieldsMessage = (): string => {
    if (currentStep === 0) return 'يرجى إدخال عنوان الإعلان أولاً.';
    if (currentStep === 1) {
      if (!formData.make_id) return 'يرجى اختيار ماركة السيارة.';
      if (selectedMakeModels.length > 0 && !formData.model_id) return 'يرجى اختيار موديل السيارة.';
    }
    if (currentStep === 2) {
      if (!formData.year) return 'يرجى إدخال سنة الصنع.';
      if (!formData.price) return 'يرجى إدخال السعر المطلوب.';
    }
    if (currentStep === 3) {
      if (!formData.city.trim()) return 'يرجى اختيار مدينة السيارة.';
      if (!formData.description.trim()) return 'يرجى كتابة وصف السيارة.';
    }
    return 'يرجى إكمال الحقول المطلوبة.';
  };

  const goNext = () => {
    if (!canMoveNext()) {
      toast({
        variant: 'destructive',
        title: 'حقل مطلوب',
        description: getMissingFieldsMessage(),
      });
      return;
    }
    setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
  };

  const generateAdvice = () => {
    setGeneratingAdvice(true);
    setTimeout(() => {
      const tips = getContextualTips('sell', 1);
      setLocalAdvice(tips[0].text_ar);
      setGeneratingAdvice(false);
    }, 800);
  };

  // ── Auth loading skeleton ────────────────────────────────────────────────────
  if (!authChecked) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4 animate-pulse">
        <div className="w-16 h-16 bg-slate-100 rounded-full" />
        <div className="h-5 w-48 bg-slate-100 rounded-full" />
        <div className="h-4 w-64 bg-slate-100 rounded-full" />
      </div>
    );
  }

  // ── Auth required screen ─────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Logo */}
        <div className="relative mb-2 flex justify-center w-full">
          <img
            src="/maknna_logo.png"
            alt="Maknna Logo"
            className="w-auto h-28 object-contain drop-shadow-sm"
          />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-slate-900">سجّل دخولك أولاً</h2>
          <p className="text-slate-500 text-sm max-w-xs leading-relaxed">
            لنشر إعلان بيع سيارتك، يجب تسجيل الدخول أو إنشاء حساب جديد.
            بعد تسجيل الدخول ستعود مباشرة لهنا لإكمال الإعلان.
          </p>
        </div>

        {/* Benefit badges */}
        <div className="flex flex-wrap justify-center gap-2">
          {[
            { icon: '🚗', label: 'نشر مجاني' },
            { icon: '⚡', label: 'ظهور فوري' },
            { icon: '👁️', label: 'آلاف المشترين' },
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full border border-blue-100">
              <span>{b.icon}</span>
              <span>{b.label}</span>
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col w-full max-w-xs gap-3 pt-2">
          <Button
            onClick={() => goToLogin('login')}
            className="h-14 rounded-2xl bg-gradient-to-l from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-black text-base shadow-lg shadow-blue-200 transition-all duration-200 hover:scale-[1.02]"
          >
            <LogIn className="h-5 w-5 ml-2" />
            تسجيل الدخول
          </Button>
          <Button
            onClick={() => goToLogin('register')}
            variant="outline"
            className="h-14 rounded-2xl border-2 border-slate-200 font-black text-base hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
          >
            <UserPlus className="h-5 w-5 ml-2" />
            إنشاء حساب جديد
          </Button>
        </div>

        <p className="text-xs text-slate-400 pt-2">
          التسجيل مجاني ويستغرق أقل من دقيقة
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
          <Check className="h-10 w-10" />
        </div>
        <h2 className="text-3xl font-bold mb-2">شكراً لك!</h2>
        <p className="text-muted-foreground mb-8">تم نشر الإعلان في سوق السيارات، ويمكن للمشترين العثور عليه من فلاتر الشراء.</p>
        <Button onClick={() => window.location.href = '/buy/results'} className="rounded-2xl h-14 px-8 text-lg">الذهاب للسوق</Button>
      </div>
    );
  }



  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-5 gap-2">
          {steps.map((step, index) => {
            const active = index === currentStep;
            const done = index < currentStep;
            return (
              <button
                key={step.title}
                type="button"
                onClick={() => setCurrentStep(index)}
                className={`rounded-2xl border p-3 text-right transition ${active ? 'border-primary bg-blue-50 text-primary' : done ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-100 bg-slate-50 text-slate-500'}`}
              >
                <span className="mb-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-black shadow-sm">
                  {done ? <Check className="h-4 w-4" /> : index + 1}
                </span>
                <span className="block text-xs font-black leading-tight">{step.title}</span>
                <span className="hidden text-[10px] font-bold opacity-70 sm:block">{step.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Card className="border border-slate-100 shadow-xl shadow-slate-200/60 rounded-3xl overflow-hidden">
        <CardHeader className="bg-slate-900 text-white pb-8 px-6 sm:px-8">
          <div className="flex items-center gap-2 mb-2">
            <Car className="h-6 w-6 text-primary" />
            <CardTitle className="text-[22px] font-headline font-black leading-tight">بيانات الإعلان</CardTitle>
          </div>
          <p className="text-white/65 text-sm font-bold leading-relaxed">يرجى التأكد من دقة البيانات لضمان وصول المشترين لك وظهور الإعلان بشكل صحيح في سوق السيارات.</p>
        </CardHeader>
        <CardContent className="pt-8 px-5 sm:px-8 bg-white">
          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep === 0 && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-24 rounded-[2rem] border-dashed border-primary flex flex-col gap-2 bg-white hover:bg-blue-50"
                    onClick={() => {
                      setCustomCity(false);
                      setImageFiles([]);
                      setImportUrl('');
                      setFormData({
                        title: '',
                        car_type_id: '',
                        make_id: '',
                        model_id: '',
                        year: '',
                        price: '',
                        mileage: '',
                        fuel_type: 'petrol',
                        transmission: 'automatic',
                        city: '',
                        description: '',
                        image_url: '',
                      });
                    }}
                  >
                    <Car className="h-6 w-6 text-primary" />
                    <span className="text-sm font-black">إعلان يدوي</span>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-24 rounded-[2rem] border-dashed border-indigo-500 flex flex-col gap-2 bg-gradient-to-br from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100"
                    onClick={() => window.location.href = '/sell/scan-my-car'}
                  >
                    <Sparkles className="h-6 w-6 text-indigo-600" />
                    <span className="text-sm font-black text-indigo-900">تسعير وفحص ذكي</span>
                  </Button>

                  <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-24 rounded-[2rem] border-dashed border-secondary flex flex-col gap-2 bg-white hover:bg-orange-50"
                      >
                        <LinkIcon className="h-6 w-6 text-secondary" />
                        <span className="text-sm font-black">استيراد من رابط</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-[2.5rem] max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-headline font-bold flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-secondary" /> استيراد إعلان من فيسبوك/إنستجرام
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-start gap-3">
                          <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <p className="text-[10px] text-slate-600 font-medium">الصق رابط الإعلان من Facebook Marketplace أو Instagram وسنقوم بجلب البيانات لك تلقائياً.</p>
                        </div>
                        <Input
                          placeholder="https://facebook.com/marketplace/item/..."
                          className="rounded-2xl h-14 bg-slate-50 border-none"
                          value={importUrl}
                          onChange={(e) => setImportUrl(e.target.value)}
                        />
                      </div>
                      <DialogFooter>
                        <Button
                          type="button"
                          onClick={handleImport}
                          disabled={importLoading || !importUrl.trim()}
                          className="w-full h-14 rounded-2xl bg-secondary hover:bg-secondary/90 font-bold"
                        >
                          {importLoading ? <Loader2 className="animate-spin h-5 w-5" /> : "ابدأ الاستيراد"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold mr-1">عنوان الإعلان</label>
                  <Input
                    placeholder="مثلاً: تويوتا كامري 2020 فل كامل"
                    className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
              </div>
            )}

            {currentStep === 1 && <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">ماركة السيارة</label>
              <Select
                required
                onValueChange={(val) => setFormData({ ...formData, make_id: val, model_id: '' })}
                value={formData.make_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                  <SelectValue placeholder="اختر الماركة" />
                </SelectTrigger>
                <SelectContent>
                  {carMakes.map(make => (
                    <SelectItem key={make.id} value={make.id}>{make.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">نوع السيارة (اختياري)</label>
              <Select
                onValueChange={(val) => setFormData({ ...formData, car_type_id: val })}
                value={formData.car_type_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                  <SelectValue placeholder="اختر نوع السيارة (سيدان، SUV...)" />
                </SelectTrigger>
                <SelectContent>
                  {carTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-bold mr-1">موديل السيارة</label>
              <Select
                onValueChange={(val) => setFormData({ ...formData, model_id: val })}
                value={formData.model_id}
                disabled={!formData.make_id}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                  <SelectValue placeholder={formData.make_id ? "اختر من الموديلات المتاحة" : "اختر الماركة أولاً"} />
                </SelectTrigger>
                <SelectContent>
                  {selectedMakeModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>{model.name_ar}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.make_id && selectedMakeModels.length === 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-[11px] font-bold text-amber-800">
                  لا توجد موديلات مفعّلة لهذه الماركة حالياً. يرجى مراجعة إدارة الموديلات من لوحة النظام.
                </div>
              )}
            </div>
            </div>}

            {currentStep === 2 && <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">سنة الصنع</label>
                <Input
                  type="number"
                  placeholder="2023"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold"
                  required
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">السعر المطلوب</label>
                <Input
                  type="number"
                  placeholder="مثلاً 85000"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">الممشى</label>
                <Input
                  type="number"
                  placeholder="مثلاً 85000"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">نوع الوقود</label>
                <Select value={formData.fuel_type} onValueChange={(val) => setFormData({ ...formData, fuel_type: val })}>
                  <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                    <SelectValue placeholder="اختر الوقود" />
                  </SelectTrigger>
                  <SelectContent>
                    {fuelTypes.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{optionLabel(item)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">ناقل الحركة</label>
                <Select value={formData.transmission} onValueChange={(val) => setFormData({ ...formData, transmission: val })}>
                  <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                    <SelectValue placeholder="اختر الناقل" />
                  </SelectTrigger>
                  <SelectContent>
                    {transmissions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{optionLabel(item)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            </div>}

            {currentStep === 3 && <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">مدينة السيارة</label>
              <Select
                value={customCity ? '__other' : formData.city}
                onValueChange={(val) => {
                  if (val === '__other') {
                    setCustomCity(true);
                    setFormData({ ...formData, city: '' });
                  } else {
                    setCustomCity(false);
                    setFormData({ ...formData, city: val });
                  }
                }}
              >
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold">
                  <SelectValue placeholder="اختر المدينة حسب الدولة" />
                </SelectTrigger>
                <SelectContent>
                  {cityOptions.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                  <SelectItem value="__other">مدينة أخرى</SelectItem>
                </SelectContent>
              </Select>
              {customCity && (
                <Input
                  placeholder="اكتب اسم المدينة"
                  className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-base font-bold"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              )}
              <p className="text-[11px] text-slate-400 font-bold">
                تظهر المدن حسب الدولة المختارة من إعدادات الموقع.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold mr-1">وصف السيارة</label>
              <Textarea
                placeholder="اذكر حالة السيارة، الممشى، والمواصفات..."
                className="min-h-[120px] rounded-2xl bg-slate-50 border-slate-200 text-base font-bold leading-relaxed"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {formData.image_url ? (
              <div className="space-y-2">
                <label className="text-sm font-bold mr-1">صورة الإعلان المستوردة</label>
                <div className="relative aspect-video w-full rounded-3xl overflow-hidden border-2 border-primary/20">
                  <img src={formData.image_url} alt="Imported" className="w-full h-full object-cover" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 left-2 rounded-full h-8 w-8"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : imageFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-bold mr-1">صور السيارة</label>
                  <label className="cursor-pointer text-xs font-black text-primary">
                    إضافة صور
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleImageFiles(e.target.files);
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {imagePreviews.map((src, index) => (
                    <div key={src} className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                      {index === 0 && (
                        <span className="absolute bottom-1 right-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-black text-white">رئيسية</span>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 left-1 h-7 w-7 rounded-full"
                        onClick={() => removeImageFile(index)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-slate-200 rounded-3xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer group">
                <div className="bg-white p-4 rounded-2xl inline-block shadow-sm group-hover:scale-110 transition-transform">
                  <Camera className="h-8 w-8 text-secondary" />
                </div>
                <p className="mt-4 font-bold text-slate-600">أضف صور السيارة</p>
                <p className="text-[10px] text-slate-400">الصور الواضحة تساعد على البيع بشكل أسرع وتظهر في سوق السيارات</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleImageFiles(e.target.files);
                    e.currentTarget.value = '';
                  }}
                />
              </label>
            )}
            </div>}

            {currentStep === 4 && <div className="space-y-5">
              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                <h3 className="mb-4 text-lg font-black text-slate-900">مراجعة الإعلان</h3>
                <div className="grid grid-cols-1 gap-3 text-sm font-bold text-slate-700 sm:grid-cols-2">
                  <p>العنوان: <span className="text-slate-950">{formData.title || '-'}</span></p>
                  <p>السنة: <span className="text-slate-950">{formData.year || '-'}</span></p>
                  <p>السعر: <span className="text-slate-950">{formData.price || '-'} OMR</span></p>
                  <p>المدينة: <span className="text-slate-950">{formData.city || '-'}</span></p>
                  <p>الوقود: <span className="text-slate-950">{formData.fuel_type}</span></p>
                  <p>الناقل: <span className="text-slate-950">{formData.transmission}</span></p>
                  <p>الصور: <span className="text-slate-950">{imageFiles.length + (formData.image_url ? 1 : 0)}</span></p>
                  <p>الرابط: <span className="text-slate-950">{importUrl ? 'موجود' : '-'}</span></p>
                </div>
              </div>
            <div className="space-y-3">
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-2xl border-dashed border-primary text-primary flex gap-2 font-bold hover:bg-blue-50 bg-white"
                  onClick={generateAdvice}
                  disabled={generatingAdvice}
                >
                  {generatingAdvice ? <Loader2 className="animate-spin h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
                  بماذا تنصحني؟
                </Button>

                {localAdvice && (
                  <div className="p-5 bg-orange-50 border border-orange-100 rounded-[2rem] animate-in fade-in slide-in-from-top-2 duration-500">
                    <div className="flex items-center gap-2 mb-2 text-secondary">
                      <Sparkles className="h-4 w-4" />
                      <h4 className="text-xs font-black uppercase">نصيحة الخبير:</h4>
                    </div>
                    <p className="text-sm font-bold text-slate-700 leading-relaxed">{localAdvice}</p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-16 rounded-2xl text-xl font-black bg-primary hover:bg-primary/90 shadow-xl shadow-primary/20"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin h-6 w-6" /> : "نشر الإعلان الآن"}
              </Button>
            </div>
            </div>}

            <div className="flex items-center gap-3 border-t border-slate-100 pt-5">
              <Button
                type="button"
                variant="outline"
                className="h-12 rounded-2xl px-5 font-black"
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((step) => Math.max(step - 1, 0))}
              >
                <ArrowRight className="h-4 w-4" /> السابق
              </Button>
              {currentStep < steps.length - 1 && (
                <Button type="button" className="h-12 flex-1 rounded-2xl font-black" onClick={goNext}>
                  التالي <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-2xl border border-amber-100">
        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
        <p className="text-[10px] text-amber-800 font-bold leading-tight">تنبيه: عند الاستيراد من رابط، قد لا يتم جلب كل البيانات بدقة. يرجى مراجعة كافة الحقول قبل النشر النهائي.</p>
      </div>
    </div>
  );
}
